import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  OperationPart as IOperationPart,
  OperationPartItem,
} from '@1po/1po-bff-fe-spec/generated/catalog/easy_basket/response/GetEasyBasketOperationDetailResponse';
import { RootState } from 'app/AppStore';
import { getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import {
  addReferenceSelection,
  getSelectedReferencesMap,
  removeReferenceSelection,
} from 'domains/easyBasket/EasyBasket.store';
import {
  BRAND_MOTRIO,
  BRAND_ORIGINAL_PARTS,
  BRAND_STANDARD_EXCHANGE,
  BRAND_VALUE_PLUS,
  OperationItemPrice,
} from 'domains/easyBasket/EasyBasket.types';
import { getPricesMap, getStocksMapV2 } from 'domains/references';
import { getDealerType, getTokenUser } from 'domains/user';
import {
  cleanDuplicatedPartsInGroups,
  mergeSingleReferenceDuplicatedGroups,
} from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/utils';
import {
  getFilteredAndSortedReferences,
  mapOperationParts,
} from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/utils';
import { Flex, WideTabs } from 'UI';
import { getCondArrayItem, getData, isLoaded } from 'utils';

const BrandsPreselection = ({ partsItems }: { partsItems: OperationPartItem[] }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [preselectedBrand, setPreselectedBrand] = useState(BRAND_ORIGINAL_PARTS);
  const selectedReferencesMap = useSelector(getSelectedReferencesMap);
  const filteredDuplicatedGroupParts = cleanDuplicatedPartsInGroups(partsItems);
  const mergedDuplicatedGroups = mergeSingleReferenceDuplicatedGroups(filteredDuplicatedGroupParts);
  const mainPartsOriginal = mergedDuplicatedGroups?.flatMap((group) => group.parts) ?? [];
  const mainRefData = mapOperationParts(mainPartsOriginal);
  const altPartsOriginal =
    mergedDuplicatedGroups?.flatMap((group) => group.parts.flatMap((p) => p.alternativeParts ?? [])) ?? [];
  const altRefNumbers = Array.from(new Set(altPartsOriginal.map((p) => p.reference)));
  const altRefData = mapOperationParts(altPartsOriginal);
  const altBrands = new Set([BRAND_MOTRIO, BRAND_VALUE_PLUS, BRAND_STANDARD_EXCHANGE]);

  const mainRefAltNumbersMap = mergedDuplicatedGroups?.reduce((acc, group) => {
    group.parts.forEach((p) => {
      acc.set(p.reference, p.alternativeParts?.map((a) => a.reference) ?? []);
    });
    return acc;
  }, new Map<string, string[]>());

  const allReferences = mainRefData.concat(altRefData);
  const allReferenceNumbers = allReferences.map((ref) => ref.referenceNumber);
  const prices = useSelector((state: RootState) => getPricesMap(state, allReferenceNumbers));
  const stocks = useSelector((state: RootState) => getStocksMapV2(state, altRefNumbers));
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const dealerType = useSelector(getDealerType);
  const tokenUserCountry = useSelector(getTokenUser)?.country;

  const filteredAltReferencesMap = mainRefData.reduce((acc, mainRef) => {
    const altNumbers = mainRefAltNumbersMap?.get(mainRef.referenceNumber) ?? [];
    if (altNumbers.length === 0) {
      acc.set(mainRef.referenceNumber, []);
      return acc;
    }
    const candidates = altRefData.filter((r) => altNumbers.includes(r.referenceNumber));
    const filtered = getFilteredAndSortedReferences({
      references: candidates,
      dealerType,
      vehicle: vehicleDetail,
      prices,
      stocks,
      country: tokenUserCountry,
    });
    acc.set(mainRef.referenceNumber, filtered);
    return acc;
  }, new Map<string, typeof altRefData>());

  const visibleBrands = new Set<string>();
  mainRefData.forEach((ref) => {
    if (ref.brand) {
      visibleBrands.add(ref.brand);
    }
    (filteredAltReferencesMap.get(ref.referenceNumber) ?? []).forEach((alt) => {
      if (alt.brand) {
        visibleBrands.add(alt.brand);
      }
    });
  });

  const tabData = [
    {
      title: t('catalog.easy_basket.tab.parts_selection.supplier.original_parts', 'Original Parts'),
      key: BRAND_ORIGINAL_PARTS,
      component: <></>,
    },
    ...getCondArrayItem(
      visibleBrands.has(BRAND_STANDARD_EXCHANGE) && {
        title: t('catalog.easy_basket.tab.parts_selection.supplier.standard_exchange', 'Standard Exchange'),
        key: BRAND_STANDARD_EXCHANGE,
        component: <></>,
      },
    ),
    ...getCondArrayItem(
      visibleBrands.has(BRAND_VALUE_PLUS) && {
        title: t('catalog.easy_basket.tab.parts_selection.supplier.value_plus', 'Value+'),
        key: BRAND_VALUE_PLUS,
        component: <></>,
      },
    ),
    ...getCondArrayItem(
      visibleBrands.has(BRAND_MOTRIO) && {
        title: t('catalog.easy_basket.tab.parts_selection.supplier.motrio', 'Motrio'),
        key: BRAND_MOTRIO,
        component: <></>,
      },
    ),
  ];

  const processPartItem = (
    partItem: IOperationPart,
    group: OperationPartItem,
    brand: string,
    isOnlyOriginalPartReference: boolean,
    isOriginalAndStandardExchangePair: boolean,
  ) => {
    const quantity = Number(partItem.quantity ?? 1);

    const addSelection = (refNumber: string) => {
      const priceRes = prices.get(refNumber);
      if (!isLoaded(priceRes)) {
        return;
      }
      const priceData = getData(priceRes);
      dispatch(
        addReferenceSelection({
          itemGroup: group.itemGroup,
          reference: {
            referenceNumber: refNumber,
            mainReferenceNumber: partItem.reference,
            quantity,
            price: priceData
              ? ({
                  currency: priceData.currency,
                  priceVatExcluded: priceData.garageView?.vatExcludedPrice,
                  priceVatIncluded: priceData.garageView?.vatIncludedPrice,
                } as OperationItemPrice)
              : undefined,
          },
        }),
      );
    };

    if (isOriginalAndStandardExchangePair) {
      const isStd = partItem.brand === BRAND_STANDARD_EXCHANGE;
      const wantsOriginal = brand === BRAND_ORIGINAL_PARTS && !isStd;
      const wantsStandardExchange = brand === BRAND_STANDARD_EXCHANGE && isStd;
      if (wantsOriginal || wantsStandardExchange) {
        addSelection(partItem.reference);
      }
      if (partItem.alternativeParts?.length === 0) {
        return;
      }
    }

    const rawAltRefs = partItem.alternativeParts ?? [];
    const visibleAlts = getFilteredAndSortedReferences({
      references: mapOperationParts(rawAltRefs),
      dealerType,
      vehicle: vehicleDetail,
      prices,
      stocks,
      country: tokenUserCountry,
    });
    const altNumbers = visibleAlts.map((r) => r.referenceNumber);

    const altMatch = visibleAlts.find(
      (ref) =>
        altNumbers.includes(ref.referenceNumber) && ref.brand === brand && isLoaded(prices.get(ref.referenceNumber)),
    );

    if (group.parts.length > 1 && !altMatch && !isOnlyOriginalPartReference) {
      return;
    }

    addSelection(altMatch?.referenceNumber ?? partItem.reference);
  };

  const handleGroupPreselection = (group: OperationPartItem, brand: string) => {
    const selectedReference = selectedReferencesMap.get(group.itemGroup);
    const groupHasAltBrand = group.parts.some(
      (p) => altBrands.has(p.brand ?? '') || p.alternativeParts?.some((ap) => altBrands.has(ap.brand ?? '')),
    );
    const isBrandOriginalPart = brand === BRAND_ORIGINAL_PARTS;
    const isOriginalPartAlreadySelected =
      isBrandOriginalPart &&
      group.parts.some(
        (p) => p.reference === selectedReference?.referenceNumber && p.brand !== BRAND_STANDARD_EXCHANGE,
      );

    if ((!groupHasAltBrand && !isBrandOriginalPart) || (isBrandOriginalPart && isOriginalPartAlreadySelected)) {
      return;
    }

    dispatch(removeReferenceSelection({ itemGroup: group.itemGroup }));

    const originalParts = group.parts.filter((p) => p.brand !== BRAND_STANDARD_EXCHANGE);
    const standardExchangeParts = group.parts.filter((p) => p.brand === BRAND_STANDARD_EXCHANGE);

    const isOriginalAndStandardExchangePair =
      group.parts.length === 2 && originalParts.length === 1 && standardExchangeParts.length === 1;

    group.parts.forEach((partItem) => {
      const isOnlyOriginalPartReference =
        isOriginalAndStandardExchangePair && partItem.reference === originalParts[0]?.reference;
      processPartItem(partItem, group, brand, isOnlyOriginalPartReference, isOriginalAndStandardExchangePair);
    });
  };

  const preselectReferences = (brand: string) => {
    setPreselectedBrand(brand);
    if (!mergedDuplicatedGroups || mergedDuplicatedGroups.length === 0) {
      return;
    }
    mergedDuplicatedGroups.forEach((group) => handleGroupPreselection(group, brand));
  };

  return (
    <Flex gap={15}>
      <WideTabs
        tabData={tabData}
        activeKey={preselectedBrand}
        onChange={preselectReferences}
        variant={'greyActiveBlack'}
        defaultActiveKey={BRAND_ORIGINAL_PARTS}
      />
    </Flex>
  );
};

export default BrandsPreselection;
